import { V2ApiError, exitCodeFor } from './api/v2-errors.js';
import { friendlyNetworkMessage } from './api/fetch-errors.js';
import { writeLog } from './log-file.js';
import { isVerbose } from './utils.js';
import { recordCliEvent, scrubLocal } from './services/telemetry.js';

export interface OptsJsonQuiet { json?: boolean; quiet?: boolean; }

/**
 * BL593 — optional hints a thrown error can carry so the top-level catch
 * in `index.ts` can enrich (or suppress) the `cli_events` row it emits.
 * Attached as plain properties on the error instance at the throw site;
 * read by {@link emitTerminalCloudGridError}.
 */
export interface TelemetryEnrichedError {
  /**
   * Set once telemetry has fired for this error (via {@link handleV2Error}),
   * so the top-level catch does not re-emit a second row for the same error.
   */
  _telemetryFired?: boolean;
  /**
   * Per-command structured payload — e.g. plug attaches `{ task_id }` on the
   * queued path or `{ trace_id }` on the in-process path. Forwarded verbatim
   * as the event's `metadata`. Privacy-safe identifiers only.
   */
  _telemetryMetadata?: Record<string, unknown>;
}

/**
 * Print a v2 API error to stderr/stdout with enough diagnostic detail that
 * a 500 from the server isn't reduced to a single opaque "Something went
 * wrong." line. Always surfaces:
 *
 *   - `status:` HTTP status code + the v2 envelope's `error.code`
 *   - `request_id:` when the server returned one (server logs are keyed
 *     on this, so the user should be able to copy-paste it into a bug
 *     report)
 *   - `hint:` per `error.details[].hint` from the v2 envelope
 *
 * The JSON envelope mirrors the same fields so machine consumers can read
 * status without parsing exit-code semantics. Backlog 061.
 *
 * Async since decision 016: every error path fires a telemetry event
 * (`event_type: 'error'`) before exit, capped at 250ms. Callers await
 * normally so the HTTP request has a chance to complete before
 * `process.exit` fires.
 */
export async function handleV2Error(err: unknown, opts: OptsJsonQuiet = {}): Promise<never> {
  // BL593 — mark the error as reported so the top-level catch (index.ts)
  // does not emit a second `cli_events` row for it. Every branch of
  // handleV2Error fires `fireErrorTelemetry` and then `process.exit`, so the
  // mark is correct regardless of which branch runs below.
  if (err && typeof err === 'object') {
    (err as Partial<TelemetryEnrichedError>)._telemetryFired = true;
  }
  if (err instanceof V2ApiError) {
    writeLog(`${err.code} ${err.status} ${err.message} request_id=${err.requestId ?? '-'}`);
    // BL595 — a maintenance 503 is NOT a failure: the substrate is briefly paused
    // for a deploy. Render the server-supplied message plain (no "Error:", no
    // stack) and exit 2 (soft — nothing is broken, retry shortly). Deliberately
    // skip the BL593 cli_events emit: maintenance is expected, not an error. The
    // `_telemetryFired` mark set above also suppresses the top-level catch's emit.
    if (err.code === 'PLUG_MAINTENANCE') {
      if (opts.json) {
        process.stdout.write(JSON.stringify({
          error: { code: err.code, message: err.message, status: err.status },
        }) + '\n');
      } else {
        process.stderr.write('CloudGrid is briefly down for maintenance — try your plug again shortly.\n');
        process.stderr.write(`  ${err.message}\n`);
      }
      process.exit(2);
    }
    // Backlog 200 — forced-update gets a dedicated multi-line renderer so the
    // user sees a clean "Run this:" call to action, not a stack-of-fields
    // error envelope. JSON callers still get the structured payload.
    if (err.code === 'CLI_UPGRADE_REQUIRED') {
      const minVersion = (err as any).minVersion as string | undefined;
      const installCommand =
        ((err as any).installCommand as string | undefined) ??
        'npm install -g @cloudgrid-io/cli@latest';
      if (opts.json) {
        process.stdout.write(JSON.stringify({
          error: {
            code: err.code,
            message: err.message,
            status: err.status,
            min_version: minVersion,
            install_command: installCommand,
          },
        }) + '\n');
      } else {
        process.stderr.write('This CLI version is no longer supported by the CloudGrid API.\n');
        if (minVersion) {
          process.stderr.write(`  Minimum supported version: ${minVersion}\n`);
        }
        process.stderr.write(`  Run: ${installCommand}\n`);
      }
      await fireErrorTelemetry({
        error_code: err.code,
        error_status: err.status,
        request_id: err.requestId ?? null,
        text: err.text ? scrubLocal(err.text) : null,
      });
      process.exit(exitCodeFor(err));
    }
    if (opts.json) {
      process.stdout.write(JSON.stringify({
        error: {
          code: err.code,
          message: err.message,
          status: err.status,
          details: err.details,
          request_id: err.requestId,
        },
      }) + '\n');
    } else if (err.code === 'RESERVED_KEY') {
      // Backlog 267 — surface the full reserved-key list so the user
      // sees the recipe, not just the code. The API attaches the list
      // via `details[].reserved_keys` (shared V2ErrorDetail). Falling
      // back to the plain renderer if the field is missing keeps the
      // CLI safe against an older API that hasn't shipped the list yet.
      const reservedDetail = err.details?.find(
        (d) => Array.isArray((d as { reserved_keys?: unknown }).reserved_keys),
      ) as { reserved_keys?: string[] } | undefined;
      const list = reservedDetail?.reserved_keys ?? [];
      process.stderr.write(`Error: ${err.message}\n`);
      if (list.length > 0) {
        process.stderr.write(`  CloudGrid reserves these keys: ${list.join(', ')}.\n`);
      }
      process.stderr.write(`  Use a different name; the reserved keys are set by the platform.\n`);
      if (err.requestId) {
        process.stderr.write(`  request_id: ${err.requestId}\n`);
      }
    } else {
      process.stderr.write(`Error: ${err.message}\n`);
      process.stderr.write(`  status: ${err.status} ${err.code}\n`);
      if (err.requestId) {
        process.stderr.write(`  request_id: ${err.requestId}\n`);
      }
      if (err.details) {
        for (const d of err.details) {
          if (d.hint) process.stderr.write(`  hint: ${d.hint}\n`);
        }
      }
    }
    await fireErrorTelemetry({
      error_code: err.code,
      error_status: err.status,
      request_id: err.requestId ?? null,
      text: err.text ? scrubLocal(err.text) : null,
    });
    process.exit(exitCodeFor(err));
  }
  const msg = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error && err.stack ? err.stack : undefined;
  // A bare `fetch failed` is the most common first-60-seconds error (offline
  // `grid login`, flaky `grid plug`). Swap it for a blameless line
  // with a next step; fall through to the raw message for everything else so
  // genuine bugs keep their detail. The narrow detector lives in fetch-errors.
  const networkMsg = friendlyNetworkMessage(err);
  writeLog(`UNHANDLED ${msg}${stack ? '\n' + stack : ''}`);
  if (!opts.json) {
    process.stderr.write(`Error: ${networkMsg ?? msg}\n`);
    // The network line is already actionable; a stack would only bury it.
    if (!networkMsg && isVerbose() && stack) {
      process.stderr.write(stack + '\n');
    }
  } else {
    process.stdout.write(
      JSON.stringify({
        error: {
          code: networkMsg ? 'NETWORK_UNREACHABLE' : 'UNKNOWN',
          message: networkMsg ?? msg,
        },
      }) + '\n',
    );
  }
  // Backlog 283 — the non-V2ApiError catch-all is the second feeder of
  // UNKNOWN-coded events (network errors, child-process failures, CLI
  // bugs). Forward the error message as `text` and the stack head as
  // `log_excerpt` so the telemetry record preserves what we'd otherwise
  // print to stderr and forget.
  await fireErrorTelemetry({
    error_code: networkMsg ? 'NETWORK_UNREACHABLE' : 'UNKNOWN',
    error_status: null,
    request_id: null,
    text: msg ? scrubLocal(msg).slice(0, 4096) : null,
    log_excerpt: stack
      ? scrubLocal(stack.split('\n').slice(0, 20).join('\n')).slice(0, 4096)
      : null,
  });
  process.exit(1);
}

/**
 * Best-effort telemetry submit for the error path. Wraps the
 * service-layer helper in a try/catch so a bug in telemetry never
 * supersedes the user-facing error message — the user already saw the
 * real failure; we exit with the code regardless.
 */
async function fireErrorTelemetry(fields: {
  error_code: string;
  error_status: number | null;
  request_id: string | null;
  /**
   * Backlog 283 — raw response body excerpt (UNKNOWN-coded V2ApiError)
   * or the thrown error's `message` (non-V2ApiError catch-all). Either
   * way, the field gives the platform something to grep on when the
   * classifier could not assign a known code.
   */
  text?: string | null;
  /**
   * Backlog 283 — first ~20 lines of the thrown error's stack on the
   * non-V2ApiError catch-all path. Already scrubbed of credential
   * patterns by the server (`telemetry-v2.ts`) and of filesystem paths
   * by `scrubLocal`.
   */
  log_excerpt?: string | null;
}): Promise<void> {
  try {
    await recordCliEvent({
      event_type: 'error',
      command: commandFromArgv(),
      error_code: fields.error_code,
      error_status: fields.error_status,
      request_id: fields.request_id,
      text: fields.text ?? null,
      log_excerpt: fields.log_excerpt ?? null,
    });
  } catch {
    // Swallow — error printout already happened, exit is next.
  }
}

/**
 * BL593 — emit a `cli_events` `error` row for a terminal `CloudGridError`
 * that reaches the top-level catch in `index.ts` without having passed
 * through {@link handleV2Error}.
 *
 * Before this hook, terminal plug failures (`PlugTaskFailedError` on the
 * queued path, a wrapped deploy error on the in-process path) — and every
 * other verb's terminal `CloudGridError` (init, login, dev) — printed
 * `Error: …` and `process.exit(1)` while emitting zero telemetry, so deploy
 * failures were invisible to the cli_events stream.
 *
 * Best-effort and never throws: the user has already seen the real error and
 * the process is about to exit. Skips entirely when the error was already
 * reported through `handleV2Error` (`_telemetryFired`), so a `V2ApiError`
 * that bubbled up cannot double-emit.
 *
 * `error_code` is the error's own `code` when it carries one, else its
 * constructor name (`PlugTaskFailedError`, `CloudGridError`, …) — a stable
 * string to filter the admin surface on. Plug attaches `_telemetryMetadata`
 * (`{ task_id }` queued / `{ trace_id }` in-process) at the throw site so the
 * row carries the deploy identifier.
 */
export async function emitTerminalCloudGridError(err: unknown): Promise<void> {
  const enriched =
    err && typeof err === 'object' ? (err as Partial<TelemetryEnrichedError>) : null;
  if (enriched?._telemetryFired) return;
  const code =
    err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : err instanceof Error
        ? err.constructor.name
        : 'UNKNOWN';
  const message = err instanceof Error ? err.message : String(err);
  try {
    await recordCliEvent({
      event_type: 'error',
      command: commandFromArgv(),
      error_code: code,
      error_status: null,
      request_id: null,
      text: message ? scrubLocal(message).slice(0, 4096) : null,
      metadata: enriched?._telemetryMetadata ?? null,
    });
  } catch {
    // Telemetry is opportunistic — never supersede the user-facing error.
  }
}

/**
 * Extract the verb from `process.argv` for the telemetry `command`
 * field. Strips flags and operands so we record `grid plug`, never
 * `grid plug --org acme my-customers-app` (decision 016 §"Things
 * this payload deliberately omits"). Returns `grid` as a fallback
 * when no positional verb is present.
 */
export function commandFromArgv(): string {
  const args = process.argv.slice(2);
  for (const a of args) {
    if (!a.startsWith('-')) return `grid ${a}`;
  }
  return 'grid';
}
